import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Alternador, Entrada, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { TIPOS_ATIVIDADE, type Etapa, type Funil, type TipoAtividade } from './tipos';
import { useWorkflows } from './ProximaTarefa';
import type { PassoWorkflow, Workflow } from './crm-sugestoes';
import { CabecalhoConfig } from './config-crm-base';
import { rotuloEtapaPipeline } from '../../nucleo/rotulos';

/**
 * CRM no novo desenho, lote 2 (complemento 5 do clone do RD): Configurações do CRM > Workflows do comercial. Vários workflows,
 * cada um com nome, funil (ou todos) e passos ("quando concluir X, na etapa Y, sugerir Z em N dias"), com prévia por passo;
 * ligar, desligar, duplicar e excluir; e a chave "Abrir Criar próxima tarefa ao concluir".
 */

type T = (k: string, v?: Record<string, unknown>) => string;

function textoDoPasso(t: T, p: PassoWorkflow) {
  return t('crm.workflows.previa', {
    quando: p.quando_tipo === 'qualquer' ? t('crm.workflows.previaQualquer') : t(`tipoAtividade.${p.quando_tipo}`),
    etapa: p.etapa_nome ? t('crm.workflows.previaEtapa', { etapa: p.etapa_nome }) : '',
    tipo: t(`tipoAtividade.${p.sugerir_tipo}`),
    assunto: p.assunto || '…',
    dias: t('crm.proxima.emDias', { count: p.dias }),
  });
}

/** Os dois workflows de exemplo do clone, no idioma de quem cria. */
function exemplos(t: T): { nome: string; passos: PassoWorkflow[] }[] {
  const e = (k: string) => t(`crm.workflows.exemplos.${k}`);
  return [
    {
      nome: e('vendaConsultiva'),
      passos: [
        { quando_tipo: 'ligacao', etapa_nome: null, sugerir_tipo: 'reuniao', assunto: e('diagnostico'), dias: 2 },
        { quando_tipo: 'reuniao', etapa_nome: null, sugerir_tipo: 'email', assunto: e('proposta'), dias: 1 },
        { quando_tipo: 'email', etapa_nome: null, sugerir_tipo: 'ligacao', assunto: e('acompanhamento'), dias: 3 },
      ],
    },
    {
      nome: e('posVenda'),
      passos: [
        { quando_tipo: 'visita', etapa_nome: null, sugerir_tipo: 'whatsapp', assunto: e('agradecer'), dias: 1 },
        { quando_tipo: 'almoco', etapa_nome: null, sugerir_tipo: 'email', assunto: e('resumo'), dias: 1 },
      ],
    },
  ];
}

export function Workflows() {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useWorkflows();
  const [editando, setEditando] = useState<{ atual?: Workflow } | null>(null);
  const [excluir, setExcluir] = useState<Workflow | null>(null);
  // Val5 (29/10): workflows são configuração do CRM (`pipeline.configurar`).
  const podeEditar = s.pode('pipeline.configurar');
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline', 'workflows'] });
  const acao = useMutation({
    mutationFn: async (f: () => Promise<unknown>) => f(),
    onSuccess: recarregar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const criar = (w: { nome: string; funil_id?: string | null; ativo?: boolean; passos: PassoWorkflow[] }) => post('/pipeline/workflows', w);

  return (
    <section className="flex min-w-0 flex-col gap-5" data-workflows="">
      {/* Lote crm7: o cabeçalho da origem (seta de voltar, título e o texto de abertura); as regras são as do MalgoOS. */}
      <CabecalhoConfig
        titulo={t('crm.configuracoes.secoes.workflows')}
        intro={
          <>
            <p>{t('crm.workflows.intro1')}</p>
            <p>{t('crm.workflows.intro2')}</p>
          </>
        }
      />
      <Estado consulta={q}>
        {(d) => (
          <>
            <label className="flex items-center gap-3 rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm">
              <Alternador
                marcado={d.abrir_proxima}
                desabilitado={!podeEditar}
                rotulo={t('crm.workflows.abrirProxima')}
                aoMudar={(v) => acao.mutate(async () => (await api('PUT', '/pipeline/workflows/preferencias', { abrir_proxima: v }), toast.sucesso(t('crm.workflows.salvo'))))}
              />
              {t('crm.workflows.abrirProxima')}
            </label>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold">{t('crm.workflows.n', { count: d.itens.length })}</p>
              {podeEditar && (
                <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setEditando({})} data-criar-workflow="">
                  {t('crm.workflows.criar')}
                </Botao>
              )}
            </div>
            {d.itens.length === 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-borda px-4 py-6 text-sm text-tinta-2">
                <span>{t('crm.workflows.vazio')}</span>
                {podeEditar && (
                  <Botao onClick={() => acao.mutate(async () => { for (const w of exemplos(t)) await criar(w); })} data-usar-exemplos="">
                    {t('crm.workflows.usarExemplos')}
                  </Botao>
                )}
              </div>
            ) : (
              <ul className="flex flex-col gap-3">
                {d.itens.map((w) => (
                  <li key={w.id} className={`rounded-xl border border-borda bg-sup-1 p-4 ${w.ativo ? '' : 'opacity-70'}`} data-workflow={w.nome}>
                    <div className="flex flex-wrap items-start gap-3">
                      <Alternador
                        marcado={w.ativo}
                        desabilitado={!podeEditar}
                        rotulo={t('crm.workflows.ligar', { nome: w.nome })}
                        aoMudar={(v) => acao.mutate(async () => (await patch(`/pipeline/workflows/${w.id}`, { ativo: v }), toast.sucesso(v ? t('crm.workflows.ligado') : t('crm.workflows.desligado'))))}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold">{w.nome}</p>
                        <p className="text-xs text-tinta-3">
                          {w.funil_nome ?? t('crm.workflows.todosFunis')} · {t('crm.workflows.passos', { count: w.passos.length })}
                          {!w.ativo && (
                            <>
                              {' '}
                              · <Selo>{t('crm.workflows.desligadoSelo')}</Selo>
                            </>
                          )}
                        </p>
                      </div>
                      {podeEditar && (
                        <div className="flex flex-wrap gap-1">
                          <Botao tamanho="sm" icone={<Pencil className="size-4" />} onClick={() => setEditando({ atual: w })}>
                            {t('crm.workflows.editar')}
                          </Botao>
                          <Botao
                            tamanho="sm"
                            icone={<Copy className="size-4" />}
                            data-duplicar=""
                            onClick={() => acao.mutate(async () => (await criar({ nome: t('crm.workflows.copia', { nome: w.nome }).slice(0, 80), funil_id: w.funil_id, ativo: w.ativo, passos: w.passos }), toast.sucesso(t('crm.workflows.duplicado'))))}
                          >
                            {t('crm.workflows.duplicar')}
                          </Botao>
                          <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.workflows.excluir')} title={t('crm.workflows.excluir')} icone={<Trash2 className="size-4" />} onClick={() => setExcluir(w)} />
                        </div>
                      )}
                    </div>
                    <ol className="mt-3 list-decimal pl-6 text-sm text-tinta-2">
                      {w.passos.map((p, i) => (
                        <li key={i}>{textoDoPasso(t, p)}</li>
                      ))}
                    </ol>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Estado>
      {editando && <FormWorkflow atual={editando.atual} aoFechar={() => setEditando(null)} aoSalvar={recarregar} />}
      <Confirmar
        aberto={!!excluir}
        aoFechar={() => setExcluir(null)}
        perigoso
        titulo={t('crm.workflows.confirmarExcluir', { nome: excluir?.nome ?? '' })}
        texto={t('crm.workflows.confirmarExcluirTexto', { count: excluir?.passos.length ?? 0 })}
        aoConfirmar={() => {
          const w = excluir;
          setExcluir(null);
          if (w) acao.mutate(async () => (await del(`/pipeline/workflows/${w.id}`), toast.sucesso(t('crm.workflows.excluido'))));
        }}
      />
    </section>
  );
}

function FormWorkflow({ atual, aoFechar, aoSalvar }: { atual?: Workflow; aoFechar: () => void; aoSalvar: () => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const id = useId();
  const [nome, setNome] = useState(atual?.nome ?? '');
  const [funil, setFunil] = useState(atual?.funil_id ?? '');
  const [passos, setPassos] = useState<PassoWorkflow[]>(atual?.passos ?? [{ quando_tipo: 'qualquer', etapa_nome: null, sugerir_tipo: 'ligacao', assunto: '', dias: 1 }]);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);
  const funis = useQuery({ queryKey: ['pipeline', 'funis'], queryFn: () => get<{ itens: Funil[] }>('/pipeline/funis') });
  const etapas = useQuery({
    queryKey: ['pipeline', 'etapas-dos-funis', funil],
    queryFn: async () => {
      const lista = funil ? [funil] : (funis.data?.itens ?? []).filter((f) => !f.arquivado_em).map((f) => f.id);
      const todas = await Promise.all(lista.map((f) => get<{ itens: Etapa[] }>('/pipeline/etapas', { funil_id: f })));
      return [...new Set(todas.flatMap((x) => x.itens.filter((e) => e.tipo === 'aberta').map((e) => e.nome)))];
    },
    enabled: !!funis.data,
  });
  const mudarPasso = (i: number, d: Partial<PassoWorkflow>) => setPassos((x) => x.map((p, j) => (j === i ? { ...p, ...d } : p)));
  const tiposQuando = ['qualquer', ...TIPOS_ATIVIDADE] as const;
  const salvar = async () => {
    const e: Record<string, string> = {};
    if (!nome.trim()) e.nome = t('crm.form.obrigatorio');
    const vazio = passos.findIndex((p) => !p.assunto.trim());
    if (vazio >= 0) e[`passo-${vazio}`] = t('crm.workflows.passoSemAssunto', { n: vazio + 1 });
    setErros(e);
    if (Object.keys(e).length) return;
    setSalvando(true);
    try {
      const corpo = { nome: nome.trim(), funil_id: funil || null, passos: passos.map((p) => ({ ...p, assunto: p.assunto.trim(), dias: Math.max(0, Math.min(365, Math.round(p.dias) || 0)) })) };
      if (atual) await patch(`/pipeline/workflows/${atual.id}`, corpo);
      else await post('/pipeline/workflows', corpo);
      toast.sucesso(atual ? t('crm.workflows.atualizado') : t('crm.workflows.criado'));
      aoSalvar();
      aoFechar();
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    } finally {
      setSalvando(false);
    }
  };
  const formId = `${id}-form`;
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={atual ? t('crm.workflows.editar') : t('crm.workflows.criar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form={formId} carregando={salvando}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        className="flex flex-col gap-4"
        data-form-workflow=""
        onSubmit={(e) => {
          e.preventDefault();
          void salvar();
        }}
      >
        <Rotulo id={`${id}-nome`} rotulo={t('crm.workflows.nome')} obrigatorio erro={erros.nome}>
          <Entrada id={`${id}-nome`} value={nome} maxLength={80} placeholder={t('crm.workflows.nomePlaceholder')} onChange={(e) => setNome(e.target.value)} aria-invalid={!!erros.nome || undefined} />
        </Rotulo>
        <Rotulo id={`${id}-funil`} rotulo={t('crm.workflows.valePara')}>
          <Selecao id={`${id}-funil`} value={funil} vazio={t('crm.workflows.todosFunis')} opcoes={(funis.data?.itens ?? []).filter((f) => !f.arquivado_em || f.id === funil).map((f) => ({ valor: f.id, rotulo: f.nome }))} onChange={(e) => setFunil(e.target.value)} />
        </Rotulo>
        <h3 className="text-sm font-semibold">{t('crm.workflows.passos')}</h3>
        <ol className="flex flex-col gap-3" data-passos="">
          {passos.map((p, i) => (
            <li key={i} className="flex flex-col gap-3 rounded-lg border border-borda p-3" data-passo={i}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">{t('crm.workflows.passo', { n: i + 1 })}</p>
                {passos.length > 1 && <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.workflows.removerPasso', { n: i + 1 })} icone={<Trash2 className="size-4" />} onClick={() => setPassos((x) => x.filter((_, j) => j !== i))} />}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Rotulo id={`${id}-q-${i}`} rotulo={t('crm.workflows.quando')}>
                  <Selecao id={`${id}-q-${i}`} data-quando="" value={p.quando_tipo} opcoes={tiposQuando.map((x) => ({ valor: x, rotulo: x === 'qualquer' ? t('crm.workflows.qualquer') : t(`tipoAtividade.${x}`) }))} onChange={(e) => mudarPasso(i, { quando_tipo: e.target.value as PassoWorkflow['quando_tipo'] })} />
                </Rotulo>
                <Rotulo id={`${id}-e-${i}`} rotulo={t('crm.workflows.etapa')}>
                  <Selecao id={`${id}-e-${i}`} value={p.etapa_nome ?? ''} vazio={t('crm.workflows.qualquerEtapa')} opcoes={(etapas.data ?? []).map((n) => ({ valor: n, rotulo: rotuloEtapaPipeline(n) }))} onChange={(e) => mudarPasso(i, { etapa_nome: e.target.value || null })} />
                </Rotulo>
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                <Rotulo id={`${id}-t-${i}`} rotulo={t('crm.workflows.sugerir')}>
                  <Selecao id={`${id}-t-${i}`} data-sugerir="" value={p.sugerir_tipo} opcoes={TIPOS_ATIVIDADE.map((x: TipoAtividade) => ({ valor: x, rotulo: t(`tipoAtividade.${x}`) }))} onChange={(e) => mudarPasso(i, { sugerir_tipo: e.target.value as TipoAtividade })} />
                </Rotulo>
                <div className="sm:col-span-2">
                <Rotulo id={`${id}-a-${i}`} rotulo={t('crm.workflows.assunto')} obrigatorio erro={erros[`passo-${i}`]}>
                  <EntradaSensivel mascara={null} livre id={`${id}-a-${i}`} data-assunto="" value={p.assunto} maxLength={200} placeholder={t('crm.workflows.assuntoPlaceholder')} aria-invalid={!!erros[`passo-${i}`] || undefined} onChange={(e) => mudarPasso(i, { assunto: e.target.value })} />
                </Rotulo>
                </div>
                <Rotulo id={`${id}-d-${i}`} rotulo={t('crm.workflows.dias')}>
                  <Entrada id={`${id}-d-${i}`} data-dias="" type="number" min={0} max={365} value={p.dias} onChange={(e) => mudarPasso(i, { dias: Number(e.target.value) })} />
                </Rotulo>
              </div>
              <p className="text-xs text-tinta-3" data-previa="">
                {textoDoPasso(t, p)}
              </p>
            </li>
          ))}
        </ol>
        <Botao
          className="self-start"
          icone={<Plus className="size-4" />}
          data-adicionar-passo=""
          onClick={() => setPassos((x) => [...x, { quando_tipo: x[x.length - 1]?.sugerir_tipo ?? 'qualquer', etapa_nome: null, sugerir_tipo: 'ligacao', assunto: '', dias: 1 }])}
        >
          {t('crm.workflows.adicionarPasso')}
        </Botao>
      </form>
    </Drawer>
  );
}
